import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import RoomSelector from 'components/Bookings2/UI/RoomSelector/RoomSelector';
import StatusButton from 'components/OrderDetail/StatusButton';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { FunctionComponent, MouseEvent } from 'react';
import styles from './DaycareTableRow.module.scss';
import PetParent from './PetParent';
import Service from './Service';
import { useMediaQuery } from 'react-responsive';
import { usePermissions, useTimeZone } from 'hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlarmExclamation } from '@fortawesome/pro-light-svg-icons';
import NotesTableRow from './NotesTableRow';
import PaidIcon from './PaidIcon';
import DepositPendingIcon from './DepositPendingIcon';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface DaycareTableRowProps {
    app: AppointmentListItem;
    isLast?: boolean;
    scrollerContainerId: string;
    className?: string;
    onCheckout?: () => void;
    onShowOrder?: () => void;
    onShowClient?: () => void;
    onShowPet?: () => void;
    onChangeRoomUnit?: (roomUnitId: number | null) => void;
    selected?: boolean;
    onToggleSelect?: (checked: boolean) => void;
}

export const DaycareTableRow: FunctionComponent<DaycareTableRowProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const timezone = useTimeZone();

    const permissions = usePermissions();
    const isCheckoutAllowed = permissions.checkout.takePayments;

    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const allowActions = isUserLoggedInMarketplace(props.app.marketplaceId);

    const hasNotes = props.app.notes.trim().length > 0;

    const checkoutOption = {
        id: 'checkout',
        label: 'Checkout',
        icon: faMoneyBill,
        onClick: () => props.onCheckout?.()
    };

    const options = isCheckoutAllowed ? [checkoutOption] : [];

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onShowOrder?.();
    };

    const date = props.app.startAt.tz(timezone);

    const petInfo = (
        <ListPetInfo
            orderId={props.app.orderId}
            pet={props.app.pet}
            onClick={allowActions ? props.onShowPet : undefined}
        />
    );

    const service = (
        <Service
            service={props.app.service}
            petId={props.app.pet.id}
            marketplaceId={props.app.marketplaceId}
        />
    );

    const room = (
        <RoomSelector
            petId={props.app.pet.id}
            serviceId={props.app.service.id}
            dropOffRoom={date.toISOString()}
            PickUpRoom={props.app.endAt.toISOString()}
            room={props.app.room}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            editable={allowActions}
            onChange={props.onChangeRoomUnit}
        />
    );

    const statusButton = (
        <StatusButton
            appointmentId={props.app.appointmentId}
            apppointmentMarketplaceId={props.app.marketplaceId}
            appointmentStatus={props.app.status}
            appointmentClosedAt={props.app.closedAt ?? undefined}
            appointmentCancelationReasonId={props.app.cancellationReasonId ?? null}
            recurringUuid={props.app.recurringUuid ?? undefined}
            customerFirstName={props.app.customer.firstName}
            customerLastName={props.app.customer.lastName}
            reloadOrderAfterStatusChange={false}
            businessAreaId={props.app.service.businessArea}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            position="fixed"
        />
    );

    const alarm = props.app.pendingDropOff ? (
        <FontAwesomeIcon
            icon={faAlarmExclamation}
            className={styles.alarm}
        />
    ) : null;

    const showCheckbox = props.onToggleSelect !== undefined;
    const columnCount = showCheckbox ? 9 : 8;

    const notMobileRow = (
        <>
            <TableRow
                className={clsx(styles.row, { [styles.withoutBorder]: hasNotes }, props.className)}
                onClick={clickHandler}
            >
                {showCheckbox && (
                    <TableCell
                        className={styles.checkboxCell}
                        onClick={event => event.stopPropagation()}
                    >
                        <Checkbox
                            checked={props.selected}
                            onChange={props.onToggleSelect}
                        />
                    </TableCell>
                )}
                <TableCell className={styles.clientNameCell}>
                    <PetParent
                        customer={props.app.customer}
                        marketplaceId={props.app.marketplaceId}
                        showOnly={!allowActions}
                        showTagsIcon={!isMobile}
                        onClick={props.onShowClient}
                    />
                </TableCell>
                <TableCell>{petInfo}</TableCell>
                <TableCell>{service}</TableCell>
                <TableCell className={styles.roomCell}>{room}</TableCell>
                <TableCell>
                    {props.app.startAt.format('MMM DD, YYYY [at] h:mm a')}
                    {alarm}
                </TableCell>
                <TableCell className={styles.priceCell}>
                    ${Number(props.app.total).toFixed(2)}
                    <DepositPendingIcon show={props.app.bookingDepositPending} />
                    <PaidIcon paid={props.app.paid} />
                </TableCell>
                <TableCell
                    width={180}
                    className={styles.statusCell}
                >
                    {statusButton}
                </TableCell>
                <TableCell
                    isActions
                    onClick={event => event.stopPropagation()}
                >
                    {allowActions && (
                        <ThreeDotsDropdown
                            options={options}
                            direction={props.isLast ? 'up' : 'down'}
                        />
                    )}
                </TableCell>
            </TableRow>
            {hasNotes && (
                <NotesTableRow
                    notes={props.app.notes}
                    colSpan={columnCount}
                />
            )}
        </>
    );

    const mobileRow = (
        <>
            <TableRow
                className={clsx(styles.mobileRow, props.className)}
                onClick={clickHandler}
            >
                <TableCell className={styles.mobileCell}>
                    <div className={styles.mobileHeader}>
                        {showCheckbox && (
                            <div
                                className={styles.mobileCheckbox}
                                onClick={event => event.stopPropagation()}
                            >
                                <Checkbox
                                    checked={props.selected}
                                    onChange={props.onToggleSelect}
                                />
                            </div>
                        )}
                        <div className={styles.mobilePetContainer}>{petInfo}</div>
                        <div className={styles.mobileDateTimeContainer}>
                            <span className={styles.mobileDateTime}>{props.app.startAt.format('MMM DD, h:mm a')}</span>
                            {alarm}
                        </div>
                    </div>
                    <div className={styles.mobileBody}>
                        {service}
                        {room}
                    </div>
                    <div className={styles.mobileFooter}>
                        {hasNotes && (
                            <NotesTableRow
                                notes={props.app.notes}
                                colSpan={8}
                            />
                        )}
                        {statusButton}
                    </div>
                </TableCell>
            </TableRow>
        </>
    );

    return isMobile ? mobileRow : notMobileRow;
};

export default DaycareTableRow;
